import { StatePage } from "@/components/state-page";
import { filterKeys, isDefinedError, withDefaults } from "@/utils";
import { orpc } from "@/utils/clients/orpc";
import { CONSTANTS } from "@/utils/constants";
import { requirePermissions, useHasPermission } from "@/utils/permissions";
import type { APIOutput } from "@/utils/types";
import { Button, useModalsStack } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { AdminDiscountCode } from "@workspace/admin-domain/src/features/discount-code/schema";
import { t } from "i18next";
import { LucideDownload, LucidePlus } from "lucide-react";
import ControlBar from "./-control-bar";
import DiscountCodeForm from "./-form";
import DiscountCodeTable from "./-table";
import { DEFAULT_DISCOUNT_CODES_PARAMS, DiscountCodeFiltersSchema } from "./-utils";

export const Route = createFileRoute("/_authenticated/discount-codes/")({
  validateSearch: DiscountCodeFiltersSchema.extend(
    withDefaults(DiscountCodeFiltersSchema, DEFAULT_DISCOUNT_CODES_PARAMS).shape,
  ),
  component: Component,
  beforeLoad: requirePermissions("discountCode:get"),
  loaderDeps: ({ search }) => [search] as const,
  loader: ({ context, deps: [search] }) => {
    void context.queryClient.prefetchQuery(
      orpc.discountCode.getAll.queryOptions({
        input: { ...DEFAULT_DISCOUNT_CODES_PARAMS, ...search },
      }),
    );
    void context.queryClient.prefetchQuery(
      orpc.discountCode.count.queryOptions({
        input: search,
      }),
    );
  },
  errorComponent: ({ error, reset }) => (
    <StatePage error={error} reset={reset} resourceType="discountCodes" emptyResourceType="discountCodes" />
  ),
  notFoundComponent: () => <StatePage isNotFound resourceType="discountCodes" emptyResourceType="discountCodes" />,
});

function Component() {
  const navigate = useNavigate();
  const stack = useModalsStack(["form", "confirm", "client", "force", "payment"]);
  const search = Route.useSearch();
  const queryClient = useQueryClient();
  const hasDiscountCodePermission = useHasPermission("discountCode:get");
  const filters = filterKeys(search, AdminDiscountCode.GetAll.shape);

  const exportAllMutation = useMutation({
    mutationFn: async () => {
      const [{ pdf, Document }, { DiscountCodePDFPage }] = await Promise.all([
        import("@react-pdf/renderer"),
        import("./-pdf-document"),
      ]);

      const agency = await queryClient.fetchQuery(orpc.agency.get.queryOptions());

      // Fetch all discount codes with pagination
      const allDiscountCodes: APIOutput["discountCode"]["getAll"] = [];
      let page = 0;
      const limit = 1000;
      let hasMore = true;

      while (hasMore) {
        const discountCodes = await queryClient.fetchQuery(
          orpc.discountCode.getAll.queryOptions({
            input: {
              ...DEFAULT_DISCOUNT_CODES_PARAMS,
              ...filters,
              page,
              limit,
              sortBy: filters.sortBy ?? DEFAULT_DISCOUNT_CODES_PARAMS.sortBy,
              sortOrder: filters.sortOrder ?? DEFAULT_DISCOUNT_CODES_PARAMS.sortOrder,
            },
          }),
        );

        allDiscountCodes.push(...discountCodes);

        if (discountCodes.length < limit) {
          hasMore = false;
        } else {
          page++;
        }
      }

      if (allDiscountCodes.length === 0) {
        throw new Error(t("discountCodes.exportPDF.all.empty"));
      }

      // Fetch full details/reservations for each code and prepare PDF pages
      const discountCodePages = await Promise.all(
        allDiscountCodes.map(async (codeSummary) => {
          const reservations = await queryClient.fetchQuery(
            orpc.reservation.getAll.queryOptions({
              input: {
                value: codeSummary.id,
                type: "discountCodeId",
              },
            }),
          );

          return (
            <DiscountCodePDFPage
              key={codeSummary.id}
              discountCode={codeSummary}
              agency={agency}
              reservations={reservations}
              baseUrl={window.location.origin}
            />
          );
        }),
      );

      const doc = <Document>{discountCodePages}</Document>;

      const blob = await pdf(doc).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().split("T")[0];
      link.download = `discount-codes-${dateStr}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    onSuccess: () => {
      notifications.show({
        title: t("success"),
        message: t("discountCodes.exportPDF.all.success.description"),
        color: "green",
      });
    },
    onError: (error) => {
      notifications.show({
        title: t("error.title"),
        message: error instanceof Error ? error.message : t("discountCodes.exportPDF.all.error.description"),
        color: "red",
      });
    },
  });

  const createMutation = useMutation(
    orpc.discountCode.create.mutationOptions({
      onError: async (error) => {
        if (isDefinedError(error) && (error.code === "FORBIDDEN" || error.code === "UNAUTHORIZED")) {
          notifications.show({
            title: t("forbidden.title"),
            message: t("forbidden.description.action"),
            color: "red",
          });

          return;
        }

        notifications.show({
          title: error.message || t("unknownError"),
          message: t("discountCodes.create.error.description"),
          color: "red",
        });
      },
    }),
  );

  return (
    <div className="gap-md md:gap-lg flex grow flex-col">
      <div className="py-md flex flex-wrap items-center justify-between gap-x-6 gap-y-4 md:justify-start">
        <p className="text-2xl font-semibold md:text-3xl">{t("discountCodes.yourDiscountCodes")}</p>
        <div className="grid grow grid-cols-2 items-center gap-1.5 md:flex">
          <Button
            variant="default"
            leftSection={<LucideDownload className="size-4" />}
            onClick={() => exportAllMutation.mutate()}
            loading={exportAllMutation.isPending}
            disabled={CONSTANTS.DEMO}
          >
            {t("export")}
          </Button>
          <DiscountCodeForm
            mode="new"
            stack={stack}
            onSubmit={async ({ payload, form }) => {
              try {
                const result = await createMutation.mutateAsync(payload);

                notifications.show({
                  title: t("success"),
                  message: t("discountCodes.create.success.description"),
                  color: "green",
                });

                form.reset();
                stack.closeAll();
                void queryClient.invalidateQueries({
                  queryKey: orpc.discountCode.getAll.key({
                    input: { ...DEFAULT_DISCOUNT_CODES_PARAMS, ...filters },
                  }),
                });

                if (hasDiscountCodePermission) {
                  void navigate({
                    to: "/discount-codes/$id",
                    params: { id: result.id },
                  });
                }
              } catch (error) {
                if (
                  isDefinedError(error) &&
                  error.data &&
                  typeof error.data === "object" &&
                  "type" in error.data &&
                  error.data.type === "ALREADY_EXISTS"
                ) {
                  form.setErrorMap({
                    onChange: {
                      fields: {
                        code: {
                          message: t("discountCodes.codeExists"),
                        },
                      },
                    },
                  });
                  return;
                }
              }
            }}
          />
          <Button onClick={() => stack.open("form")} leftSection={<LucidePlus />}>
            {t("discountCodes.new")}
          </Button>
        </div>
      </div>
      <ControlBar />
      <DiscountCodeTable />
    </div>
  );
}
